// Studio chat-only build mode (Live Motion Build): ?chat=1&draft=<chat draft uuid>&job=<build job id>.
// Ids are validated before they become headers/socket payloads; mw re-checks ownership.
import { useSyncExternalStore } from "react";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const JOB = /^[\w-]{1,128}$/;

export function readBuildParams(
  search = typeof window !== "undefined" ? window.location.search : ""
) {
  try {
    const q = new URLSearchParams(search);
    const draftId = q.get("draft") || "";
    const jobId = q.get("job") || "";
    if (q.get("chat") !== "1" || !UUID.test(draftId) || !JOB.test(jobId)) return null;
    return { draftId, jobId };
  } catch (e) {
    return null;
  }
}

// Same URL without the build-mode params, so a reload opens the finished course normally.
export function stripBuildParams(search) {
  const q = new URLSearchParams(search);
  ["chat", "draft", "job", "code"].forEach((k) => q.delete(k));
  const out = q.toString();
  return out ? `?${out}` : "";
}

// After a Continue the URL's ?job= must name the new job, or a reload replays the paused one (pause card again).
export function replaceBuildJob(search, jobId) {
  const q = new URLSearchParams(search);
  if (!q.has("job")) return search;
  q.set("job", jobId);
  return `?${q.toString()}`;
}

// R10 restore: put the build params in the URL so a reload takes the normal ?chat=1 boot and Continue can swap ?job=.
export function withBuildParams(search, { draftId, jobId }) {
  const q = new URLSearchParams(search);
  q.set("chat", "1");
  q.set("draft", draftId);
  q.set("job", jobId);
  return `?${q.toString()}`;
}

// ?id-only open of a course whose build runs or is paused (R10): the build state EditorApp restores, or null.
// Needs the chat draft (BuildChat's thread) and must be this course's build (mw also matches an ?id=<chat draft> open).
// Never resumed: the job's replay reveals the editor (and the pause card).
export function restoredBuildState(active) {
  if (
    !active ||
    typeof active.jobId !== "string" ||
    !JOB.test(active.jobId) ||
    !UUID.test(String(active.draftTrainingId || ""))
  )
    return null;
  if (active.idealStudioTrainingId !== window.idealStudioTrainingId) return null;
  return {
    buildParams: { draftId: active.draftTrainingId, jobId: active.jobId },
    buildResumed: false,
    buildRevealed: false,
  };
}

// Job events expired (24h) but the course doc already has scenes: a normal editor open.
export function expiredWithScenes(feed, navItemsIds) {
  return (
    !!feed.items.some((i) => i.kind === "expired" && i.empty) &&
    (navItemsIds || []).some((id) => id !== 0)
  );
}

// Expired job: build mode skipped the mount import, so import first, then decide on the
// imported scene ids. true = the course has scenes (open normally); false = stay locked.
export function resolveExpired(feed, importScenes) {
  return Promise.resolve(importScenes()).then(
    (ids) => expiredWithScenes(feed, ids),
    () => false
  );
}

// Scene-list ghosts ("(building)" rows). UI-only: never dispatched to Redux, never in
// exportedState, so autosave and undo never see them. BuildChat writes, BuildGhosts reads.
// ponytail: module store with one writer; same shape as narrationDraftStore.js.
let ghosts = { planned: [], failed: [], done: true };
const listeners = new Set();

export function setBuildGhosts(next) {
  ghosts = next;
  listeners.forEach((fn) => fn());
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useBuildGhosts() {
  return useSyncExternalStore(
    subscribe,
    () => ghosts,
    () => ghosts
  );
}

// Build lock: from page load in build mode until EditorApp._onBuildFinished. While on,
// ideaEnvSaveState refuses every save, autosave cannot resume, and structural
// scene-list/timeline edits are ignored (scene clicks + playhead scrub stay allowed).
let buildLocked = typeof window !== "undefined" && !!readBuildParams();

export function isBuildLocked() {
  return buildLocked;
}

export function setBuildLocked(next) {
  buildLocked = !!next;
  listeners.forEach((fn) => fn());
}

export function useBuildLocked() {
  return useSyncExternalStore(subscribe, isBuildLocked, isBuildLocked);
}

// Notice pinned in the standard ChatPanel after the reveal: { title, line } strings
// (frozen → ready/stop). BuildChat writes, BuildNotice reads; null = nothing to show.
let buildNotice = null;

export function setBuildNotice(next) {
  buildNotice = next;
  listeners.forEach((fn) => fn());
}

export function useBuildNotice() {
  return useSyncExternalStore(
    subscribe,
    () => buildNotice,
    () => buildNotice
  );
}

// A rebuild / continue requested from a chat confirm card is in flight: the pinned pause card's buttons freeze too
// (BuildThread writes, PauseCard reads), so nothing can be double-submitted from the other surface.
let pauseFrozen = false;

export function setPauseFrozen(next) {
  if (pauseFrozen === next) return;
  pauseFrozen = next;
  listeners.forEach((fn) => fn());
}

export function usePauseFrozen() {
  return useSyncExternalStore(
    subscribe,
    () => pauseFrozen,
    () => pauseFrozen
  );
}

// course_build_progress steps (mw CourseBuildStep) → share of the scene in flight.
export const BUILD_STEPS = ["narration", "images", "video", "motion", "saving"];
// course_build_step phases (mw scene agent) shown as the live status line; "saved" is not a line.
export const AGENT_PHASES = ["style_bible", "plan", "generate", "compose", "review", "fix"];
const STEP_PCT = { narration: 0.2, images: 0.5, video: 0.8, motion: 0.8, saving: 0.95 };

// Whole-build progress 0..100: finished scenes (ready or failed) + the live scene's step share.
export function buildProgress(feed) {
  if (feed.done) return 100;
  if (!feed.total) return 0;
  const finished = feed.ready.length + feed.failed.length;
  const live = feed.live;
  const inFlight =
    live && !feed.failed.includes(live.index) && !feed.ready.some((r) => r.index === live.index)
      ? STEP_PCT[live.step] || 0
      : 0;
  return Math.min(100, Math.round(((finished + inFlight) / feed.total) * 100));
}

// Per-scene checklist. "images" means the images arrived, so it counts as done.
export function stepChecklist(step) {
  const at = BUILD_STEPS.indexOf(step);
  return BUILD_STEPS.map((key, k) => ({
    key,
    state: k < at || (k === at && key === "images") ? "done" : k === at ? "active" : "pending",
  }));
}

// Hero image: the user's pick for this scene if still present, else the newest (auto-follow).
export function heroUrl(imgs, pick, index) {
  if (pick && pick.index === index && imgs.includes(pick.url)) return pick.url;
  return imgs[imgs.length - 1];
}

// mw cuts the narration snippet at a fixed char count (COURSE_BUILD_SNIPPET_CHARS); a
// text that long was cut, so end it at the last word boundary with an ellipsis.
export function truncateWords(text, max = 280) {
  const s = String(text || "");
  if (s.length < max) return s;
  const cut = s.slice(0, max);
  const sp = cut.search(/\s\S*$/);
  return `${(sp > 0 ? cut.slice(0, sp) : cut).replace(/[\s,.;:–-]+$/, "")}…`;
}

export const formatElapsed = (ms) => {
  const sec = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
};

// Planned scenes still to come: not in the doc yet, not failed, build not finished.
export function ghostItems(g, realIds) {
  if (!g || g.done) return [];
  const real = new Set(realIds || []);
  const failed = g.failed || [];
  const ready = g.ready || []; // by index too: a re-mapped doc may not keep the planned navIds
  return (g.planned || []).filter(
    (p) => !real.has(p.navId) && !failed.includes(p.index) && !ready.includes(p.index)
  );
}

// mw sent course_build_done (even built = 0): the build is over and the editor must unlock.
export const buildEnded = (feed) => feed.items.some((i) => i.key === "done");
